import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import ProductService from "services/productService";

// Initial state for products slice
const initialState = {
  loading: false,
  products: [],
  selectedProduct: { en: null, ar: null },
  options: [],
  total: 0, // For pagination total count
  successMessage: "",
  errorMessage: "",
};

export const getProducts = createAsyncThunk(
  "products/getProducts",
  async ({ page = 1, languageId = "1", search = "", categoryId = "" }, { rejectWithValue }) => {
    try {
      const response = await ProductService.getProducts(
        page,
        { "Language-Id": languageId },
        { search, category_id: categoryId }
      );
      return {
        products: response.data.results,
        total: response.data.count,
      };
    } catch (err) {
      return rejectWithValue(err.message || "Error fetching products");
    }
  }
);
export const getProduct = createAsyncThunk(
  "products/getProduct",
  async (productId, { rejectWithValue }) => {
    try {
      // Fetch product in English
      const responseEn = await ProductService.getProduct(productId, 1); // English

      // Fetch product in Arabic
      const responseAr = await ProductService.getProduct(productId, 4); // Arabic

      // Return both responses
      return {
        responseEn: responseEn,
        responseAr: responseAr,
      };
    } catch (error) {
      // Handle the error using `rejectWithValue`
      return rejectWithValue(error.message || "Error fetching products");
    }
  }
);

// Async thunk to fetch banners
export const postProducts = createAsyncThunk(
  "products/postProducts",
  async (params, { rejectWithValue }) => {
    try {
      const response = await ProductService.createProduct(params);
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);
// Async thunk to fetch Products
export const updateProducts = createAsyncThunk(
  "products/updateProducts",
  async ({ data, productId }, { rejectWithValue }) => {
    try {
      const response = await ProductService.updateProduct(data, productId);
      // console.log(response, "response+++1");

      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

// Async thunk to delete a product

export const deleteProduct = createAsyncThunk(
  "products/deleteProduct",
  async (
    { productId, languageId, currentPage },
    { rejectWithValue, dispatch }
  ) => {
    try {
      await ProductService.deleteProduct(productId);

      // Dispatch the getProducts action to refetch the updated list of products with current language and page
      await dispatch(getProducts({ page: currentPage, languageId }));

      return { productId, message: "Product deleted successfully" };
    } catch (err) {
      return rejectWithValue(err.message || "Error deleting product");
    }
  }
);

export const setProductStatus = createAsyncThunk(
  "products/setProductStatus",
  async (
    { productId, status, currentPage, langId },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await ProductService.setProductStatus(productId, status);
      await dispatch(getProducts({ page: currentPage, languageId: langId }));
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const getOptionsValues = createAsyncThunk(
  "products/getOptionsValues",
  async (languageId, { rejectWithValue }) => {
    try {
      const response = await ProductService.getOptionsValues(languageId);
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error fetching options");
    }
  }
);

// Create the slice for products

const productSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    clearMessage: (state) => {
      state.successMessage = "";
      state.errorMessage = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle getProducts lifecycle
      .addCase(getProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProducts.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.products = payload.products;
        state.total = payload.total;
        state.showMessage = false;
      })
      .addCase(getProducts.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })

      // Handle getProduct lifecycle
      .addCase(getProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProduct.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.selectedProduct = payload;
      })
      .addCase(getProduct.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })

      // Handle postProducts lifecycle
      .addCase(postProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(postProducts.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.successMessage = "Product created successfully";
      })
      .addCase(postProducts.rejected, (state, { payload }) => {
        state.loading = false;
        state.errorMessage = payload;
      })

      // Handle updateProducts lifecycle
      .addCase(updateProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProducts.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.successMessage = "Product updated successfully";
      })
      .addCase(updateProducts.rejected, (state, { payload }) => {
        state.loading = false;
        state.errorMessage = payload;
      })

      // Handle setProductStatus lifecycle
      .addCase(setProductStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(setProductStatus.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.message = payload.message;
        state.showMessage = true;
      })
      .addCase(setProductStatus.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })

      // Handle deleteProduct lifecycle
      .addCase(deleteProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteProduct.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.message = payload.message;
        state.showMessage = true;
        // Refetch the products after deletion
        state.products = state.products.filter(
          (p) => p.id !== payload.productId
        );
        state.total -= 1;
      })
      .addCase(deleteProduct.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })

      // Handle getOptionsValues lifecycle
      .addCase(getOptionsValues.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOptionsValues.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.options = payload;
      })
      .addCase(getOptionsValues.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      });
  },
});

// Export actions and reducer

export const { clearMessage } = productSlice.actions;
export default productSlice.reducer;
